// Settings, opened from the rail: a card for each group (the reader's own
// account last), and one group at a time with its pages (SettingsGroup.jsx).
// `?setting=` is the address of what is open (lib/settingsModel.js). An old
// link that names a page (adminView) becomes its address in place, once.
// Dashboard chat opens as a dialog over the home rather than a page.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMyPermissions } from "../../lib/permissions.js";
import { AUTH_REQUIRED, openAiIntegrationEnabled } from "../../lib/featureFlags.js";
import { ROUTER_PARAM, requestedSetting } from "../../lib/routeModel.js";
import {
    ACCOUNT, LEGACY_SETTING_PARAMS, resolveSetting, searchGroups, searchSettings, settingFromLegacy, visibleAccount, visibleGroups,
} from "../../lib/settingsModel.js";
import LogoutDialog from "../LogoutDialog.jsx";
import { ScrollRoot } from "../ui/scrollRoot.js";
import SettingsTop from "./SettingsTop.jsx";
import SettingsHome from "./SettingsHome.jsx";
import SettingsResults from "./SettingsResults.jsx";
import SettingsGroup from "./SettingsGroup.jsx";
import ChatSettingsDialog from "./ChatSettingsDialog.jsx";
import { useGroupSummary, useWaitingRequests } from "./settingsSummaries.js";

const CHAT = "chat";

export default function SettingsConsole({ entitlements = null, theme = "dark", onToggleTheme = null, onScopeNavigate = null }) {
    const [params, setParams] = useSearchParams();
    const caps = useMyPermissions();
    const flags = useMemo(() => ({ openai: openAiIntegrationEnabled() }), []);
    const groups = useMemo(() => visibleGroups({ caps, entitlements, flags }), [caps, entitlements, flags]);
    const account = useMemo(() => visibleAccount({ caps, entitlements, flags }), [caps, entitlements, flags]);
    const [query, setQuery] = useState("");
    const searchRef = useRef(null);
    const [logoutOpen, setLogoutOpen] = useState(false);

    // An old link names a page: give it its address in place, once.
    const legacy = settingFromLegacy(params);
    useEffect(() => {
        if (!legacy) return;
        setParams((current) => {
            const next = new URLSearchParams(current);
            for (const key of LEGACY_SETTING_PARAMS) next.delete(key);
            next.set(ROUTER_PARAM.SETTING, legacy);
            return next;
        }, { replace: true });
    }, [legacy, setParams]);

    const address = requestedSetting(params) ?? legacy;
    const { group: resolved, section, page, tab } = resolveSetting(address, groups, account);
    const chatOpen = resolved?.id === CHAT;
    // Dashboard chat is a dialog over the home, so the page under it is the home.
    const group = chatOpen ? null : resolved;

    function go(next) {
        setQuery("");
        setParams((current) => {
            const out = new URLSearchParams(current);
            for (const key of LEGACY_SETTING_PARAMS) out.delete(key);
            if (next) out.set(ROUTER_PARAM.SETTING, next);
            else out.delete(ROUTER_PARAM.SETTING);
            return out;
        });
    }

    // Opening a group or another page starts at its top. A long table fits
    // the room left below it here and scrolls its own rows (ScrollRoot).
    const scrollRef = useRef(null);
    const scrollRoot = useCallback(() => scrollRef.current, []);
    useEffect(() => { scrollRef.current?.scrollTo?.({ top: 0 }); }, [group?.id, page?.id]);

    // On the home, the reader is most likely here to find something: the
    // search has the focus when Settings opens and when they come back to it.
    useEffect(() => {
        if (!group && !chatOpen) searchRef.current?.focus({ preventScroll: true });
    }, [group, chatOpen]);

    const hits = useMemo(() => searchSettings(query, [...groups, account]), [query, groups, account]);
    const groupHits = useMemo(() => searchGroups(query, [...groups, account]), [query, groups, account]);
    const waiting = useWaitingRequests(groups.some((g) => g.id === "access" && g.sections.some((s) => s.id === "requests")) && !group);
    // Read again as sections open and close, so a value changed in one is current.
    const summary = useGroupSummary(group?.id === ACCOUNT ? null : group, section?.id ?? "");

    const bodyProps = {
        entitlements, onOpen: go, onScopeNavigate, theme, onToggleTheme,
        onLogout: AUTH_REQUIRED ? () => setLogoutOpen(true) : null,
    };
    // The home's cards: the organization's groups, then the reader's own.
    const cards = useMemo(() => [...groups, account], [groups, account]);

    const results = query.trim()
        ? <SettingsResults query={query.trim()} hits={hits} groupHits={groupHits} onOpen={go} />
        : null;

    return (
        <ScrollRoot.Provider value={scrollRoot}>
            <section ref={scrollRef} data-settings-console className="h-full min-h-0 min-w-0 overflow-y-auto bg-[color:var(--bg)]">
                <div className="flex min-h-full w-full flex-col px-[var(--main-inset)]">
                    {!group ? (
                        <SettingsHome
                            groups={cards}
                            onOpen={go}
                            waiting={waiting}
                            query={query}
                            onQuery={setQuery}
                            searchRef={searchRef}
                            results={results}
                        />
                    ) : (
                        <>
                            <SettingsTop onBack={() => go(null)} query={query} onQuery={setQuery} searchRef={searchRef} />
                            {results ?? (
                                <SettingsGroup group={group} page={page} tab={tab} onOpen={go} bodyProps={bodyProps} summary={summary} />
                            )}
                        </>
                    )}
                </div>
                <ChatSettingsDialog open={chatOpen} tab={section?.id ?? null} onClose={() => go(null)} />
                {AUTH_REQUIRED ? <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} /> : null}
            </section>
        </ScrollRoot.Provider>
    );
}
